import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { buildApiUrl, buildAssetUrl } from '../config/constants';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  status: string;
  isPromoted: boolean;
  featuredImage?: string;
  readTime: number;
  viewCount: number;
  publishedAt: string;
  createdAt: string;
  author: {
    id: number;
    username: string;
    email: string;
  };
  category?: {
    id: number;
    name: string;
    slug: string;
    color: string;
  };
  tags: Array<{
    id: number;
    name: string;
    slug: string;
    color: string;
  }>;
}

interface BlogCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  color: string;
  postCount: number;
}

interface BlogTag {
  id: number;
  name: string;
  slug: string;
  color: string;
  postCount: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalPosts: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const BlogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalPosts: 0,
    hasNext: false,
    hasPrev: false
  });

  // URL params
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedTag = searchParams.get('tag') || '';
  const searchTerm = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, selectedTag, searchTerm, currentPage]);

  useEffect(() => {
    fetchFeaturedPosts();
    fetchCategories();
    fetchTags();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '9'
      });

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedTag) {
        params.append('tag', selectedTag);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(buildApiUrl(`/blog?${params}`));
      if (!response.ok) {
        throw new Error('Nu s-au putut încărca postările');
      }

      const data = await response.json();
      setPosts(data.data.posts);
      setPagination(data.data.pagination);
    } catch (error) {
      console.error('Eroare la încărcarea postărilor:', error);
      setError('Nu s-au putut încărca postările');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedPosts = async () => {
    try {
      const response = await fetch(buildApiUrl('/blog/featured?limit=3'));
      if (response.ok) {
        const data = await response.json();
        setFeaturedPosts(data.data);
      }
    } catch (error) {
      console.error('Eroare la încărcarea postărilor promovate:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(buildApiUrl('/blog/categories'));
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Eroare la încărcarea categoriilor:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(buildApiUrl('/blog/tags'));
      if (response.ok) {
        const data = await response.json();
        setTags(data.data);
      }
    } catch (error) {
      console.error('Eroare la încărcarea tag-urilor:', error);
    }
  };

  const handleCategoryChange = (category: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (category === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    newParams.delete('page'); // Reset la pagina 1
    setSearchParams(newParams);
  };

  const handleTagClick = (tag: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedTag === tag) {
      newParams.delete('tag');
    } else {
      newParams.set('tag', tag);
    }
    newParams.delete('page'); // Reset la pagina 1
    setSearchParams(newParams);
  };

  const handleSearch = (term: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (term) {
      newParams.set('search', term);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page'); // Reset la pagina 1
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          {/* Hero Section with Featured Posts */}
          {featuredPosts.length > 0 && !searchTerm && selectedCategory === 'all' && !selectedTag && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Postări Recomandate</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {featuredPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group block bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    {post.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                        <img
                          src={buildAssetUrl(post.featuredImage)}
                          alt={post.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      {post.category && (
                        <span 
                          className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium mb-3"
                          style={{ backgroundColor: post.category.color }}
                        >
                          {post.category.name}
                        </span>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>de {post.author.username}</span>
                        <div className="flex items-center space-x-4">
                          <span>{post.readTime} min citire</span>
                          <span>{formatDate(post.publishedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="lg:w-3/4">
              {/* Search and Filter Bar */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Caută în blog..."
                      defaultValue={searchTerm}
                      onChange={(e) => {
                        const timer = setTimeout(() => handleSearch(e.target.value), 500);
                        return () => clearTimeout(timer);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <select
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Toate categoriile</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.slug}>
                          {category.name} ({category.postCount})
                        </option>
                      ))}
                    </select>

                    {(selectedCategory !== 'all' || selectedTag || searchTerm) && (
                      <button
                        onClick={clearFilters}
                        className="px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        Șterge filtrele
                      </button>
                    )}
                  </div>
                </div>

                {/* Active Filters */}
                {(selectedCategory !== 'all' || selectedTag || searchTerm) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {searchTerm && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                        Căutare: "{searchTerm}"
                        <button
                          onClick={() => handleSearch('')}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedCategory !== 'all' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                        Categorie: {categories.find(c => c.slug === selectedCategory)?.name}
                        <button
                          onClick={() => handleCategoryChange('all')}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedTag && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                        Tag: {selectedTag}
                        <button
                          onClick={() => handleTagClick(selectedTag)}
                          className="ml-2 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              {/* Loading State */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">Se încarcă postările...</p>
                </div>
              ) : (
                <>
                  {/* Results Info */}
                  <div className="mb-6 text-gray-600">
                    {pagination.totalPosts > 0 ? (
                      <>
                        Afișez {posts.length} din {pagination.totalPosts} postări
                        {(selectedCategory !== 'all' || selectedTag || searchTerm) && ' pentru filtrele selectate'}
                      </>
                    ) : (
                      'Nu au fost găsite postări'
                    )}
                  </div>

                  {/* Posts Grid */}
                  {posts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                      {posts.map((post) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className="group block bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300"
                        >
                          {post.featuredImage && (
                            <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                              <img
                                src={buildAssetUrl(post.featuredImage)}
                                alt={post.title}
                                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          )}
                          <div className="p-6">
                            {post.category && (
                              <span 
                                className="inline-block px-2 py-1 rounded text-white text-xs font-medium mb-3"
                                style={{ backgroundColor: post.category.color }}
                              >
                                {post.category.name}
                              </span>
                            )}
                            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                              {truncateText(post.title, 60)}
                            </h3>
                            {post.excerpt && (
                              <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                                {truncateText(post.excerpt, 120)}
                              </p>
                            )}
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{post.author.username}</span>
                              <div className="flex items-center space-x-2">
                                <span>{post.readTime} min</span>
                                <span>•</span>
                                <span>{post.viewCount} vizualizări</span>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-400">
                              {formatDate(post.publishedAt)}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-6xl text-gray-300 mb-4">📝</div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2">
                        Nu au fost găsite postări
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {searchTerm || selectedCategory !== 'all' || selectedTag
                          ? 'Încearcă să modifici filtrele sau să cauți altceva.'
                          : 'Nu există încă postări publicate în blog.'}
                      </p>
                      {(selectedCategory !== 'all' || selectedTag || searchTerm) && (
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Vezi toate postările
                        </button>
                      )}
                    </div>
                  )}

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Anterior
                      </button>
                      
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter(page => 
                          page === 1 || 
                          page === pagination.totalPages || 
                          Math.abs(page - pagination.currentPage) <= 2
                        )
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="px-2 text-gray-500">...</span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`px-4 py-2 border rounded-lg ${
                                page === pagination.currentPage
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))
                      }
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Următor
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/4 space-y-6">
              {/* Categories */}
              {categories.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Categorii</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className={`block w-full text-left px-3 py-2 rounded transition-colors ${
                        selectedCategory === 'all'
                          ? 'bg-blue-100 text-blue-700'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      Toate ({categories.reduce((total, cat) => total + cat.postCount, 0)})
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryChange(category.slug)}
                        className={`flex items-center justify-between w-full text-left px-3 py-2 rounded transition-colors ${
                          selectedCategory === category.slug
                            ? 'bg-blue-100 text-blue-700'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded mr-2"
                            style={{ backgroundColor: category.color }}
                          ></div>
                          <span>{category.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">({category.postCount})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Tags */}
              {tags.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tag-uri Populare</h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 20).map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => handleTagClick(tag.slug)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          selectedTag === tag.slug
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag.name} ({tag.postCount})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage; 
